<template>
  <div class="flex items-center justify-center gap-x-2">

    <Spinner v-if="isUploadingAudio" class="w-5 h-5" />

    <div class="flex items-center justify-center gap-x-2" v-else>
      <label :for="inputId" class="cursor-pointer"  v-if="!isLocalizePage || (isLocalizePage && audioSource)">
        <img src="@/assets/icons/audio.svg" alt="audio" class="min-w-5 h-5" />
      </label>
      <input :id="inputId" type="file" @change="handleFileSelection" accept="audio/wav,audio/mp3" class="hidden" />

      <div v-if="audioSource" class="flex justify-center gap-x-2">
        <button @click.prevent="toggleAudio" class="p-2 bg-green-500 text-white rounded-full" >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon v-if="!isPlaying" points="5 3 19 12 5 21 5 3"></polygon>
            <rect v-else x="6" y="6" width="12" height="12"></rect>
          </svg>
        </button>
         <button v-if="audioSource" @click.prevent="downloadAudio" class="">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
             <circle cx="14" cy="14" r="14" fill="#337DD6"/>
             <path d="M14.0001 16.4L10.0001 12.65L11.1201 11.5625L13.2001 13.5125V7.40002H14.8001V13.5125L16.8801 11.5625L18.0001 12.65L14.0001 16.4ZM9.2001 19.4C8.7601 19.4 8.38356 19.2533 8.0705 18.9598C7.75743 18.6663 7.60063 18.313 7.6001 17.9V15.65H9.2001V17.9H18.8001V15.65H20.4001V17.9C20.4001 18.3125 20.2436 18.6658 19.9305 18.9598C19.6174 19.2538 19.2406 19.4005 18.8001 19.4H9.2001Z" fill="white"/>
            </svg>

        </button>
        <button v-if="!isLocalizePage" @click.prevent="deleteAudio" class="p-2 bg-red-500 text-white rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>

      <audio ref="audioPlayer" :src="audioSource" @ended="isPlaying = false" class="hidden"></audio>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useToast } from "vue-toastification";
import MediaService from "@/services/mediaService";
import { useRoute } from "vue-router"; 
import { useI18n } from "vue-i18n";
export default defineComponent({
  name: "AudioUploader",
  emits: ["audio-uploaded", "audio-deleted"],
  props: {
    mediaLabel: {
      type: String,
      required: true,
    },
    audioUrl: {
      type: String,
      default: null,
    },
    
  },
    setup() {
    const route = useRoute();
    const isLocalizePage = computed(() => route.fullPath.includes("localize"));
    const { t } = useI18n();
    return {
      isLocalizePage,
      t,
    };
  },
  data(props) {
    return {
      selectedFile: null as File | null,
      uploadedAudioUrl: "",
      uploadStatus: "",
      isUploadingAudio: false,
      inputId: `audio-input-${Math.random().toString(36).substring(7)}`,
      isPlaying: false,
      audioLabel: props.audioLabel,
      toast: useToast(),
      
      
    };
  },
  computed: {
    audioSource(): string {
      return this.uploadedAudioUrl || this.audioUrl;
    },
    
  },
  methods: {
    handleFileSelection(event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.selectedFile = target.files[0];
        this.uploadAudio();
      }
    },
async downloadAudio() {
  if (!this.audioSource) {
    this.toast.error("No audio available to download.");
    return;
  }

  try {
    const response = await fetch(this.audioSource);
    
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const blob = await response.blob();

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;

    // Derive a clean filename
    const filename = this.audioSource.split("/").pop() || "audio-file";
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Release the blob URL after download
    window.URL.revokeObjectURL(url);

   this.toast.success(this.t("localization.audiodownloadedsuccessfully"));
  } catch (error) {
    console.error("Download error:", error);
    this.toast.error("Failed to download audio file.");
  }
},



async uploadAudio() {
  if (!this.selectedFile) return;

  try {
    this.isUploadingAudio = true;

    const result = await MediaService.uploadMedia(this.selectedFile, this.mediaLabel);

    if (result.status === true && result.data) {
      // Narrow the type: if it's an array, pick the first item
      const fileData: UploadResponseData =
        Array.isArray(result.data) ? result.data[0] : result.data;

      this.uploadedAudioUrl = fileData.preview_url;
      this.$emit("audio-uploaded", fileData);
      this.toast.success(result.message);
    } else {
      this.uploadStatus = result.message || "Upload failed";
      this.toast.error(result.message);
    }
  } catch (error) {
    this.uploadStatus = "An error occurred during upload";
    console.error("Upload error:", error);
    this.toast.error("An error occurred during upload");
  } finally {
    this.isUploadingAudio = false;
  }
},

    toggleAudio() {
      const audioPlayer = this.$refs.audioPlayer as HTMLAudioElement;
      if (this.isPlaying) {
        audioPlayer.pause();
      } else {
        audioPlayer.play();
      }
      this.isPlaying = !this.isPlaying;
    },
    stopAudio() {
      const audioPlayer = this.$refs.audioPlayer as HTMLAudioElement;
      audioPlayer.pause();
      audioPlayer.currentTime = 0;
      this.isPlaying = false;
    },
    deleteAudio() {
      this.stopAudio();
      this.uploadedAudioUrl = "";
      this.$emit("audio-deleted");
    },
  },
  beforeUnmount() {
    this.stopAudio();
  },
  
});

</script>
